import { ForceUpdateGate } from "./components/ForceUpdateGate";
import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { api, setAuthToken } from "./api";
import { initKeyboardFix } from "./lib/keyboardFix";
import { Toaster } from "./components/ui/sonner";
import { AppProvider, useApp } from "./context/AppContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./components/ui/alert-dialog";
import { Shell } from "./components/Shell";
import LoginPage from "./pages/LoginPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import SupportPage from "./pages/SupportPage";
import Home from "./pages/Home";
import MessagesPage from "./pages/MessagesPage";
import ChatThread from "./pages/ChatThread";
import JobsPage from "./pages/JobsPage";
import JobHub from "./pages/JobHub";
import IssuesPage from "./pages/IssuesPage";
import TasksPage from "./pages/TasksPage";
import ActivityPage from "./pages/ActivityPage";
import MorePage from "./pages/MorePage";
import SettingsPage from "./pages/SettingsPage";
import DailySummaryPage from "./pages/DailySummaryPage";
import SearchPage from "./pages/SearchPage";
import ProfilePage from "./pages/ProfilePage";
import AppleCallbackPage from "./pages/AppleCallbackPage";
import GoogleCallbackPage from "./pages/GoogleCallbackPage";
import AdminPage from "./pages/AdminPage";
import { ErrorBoundary } from "./components/ErrorBoundary";

function Protected({ children }) {
  const { user, authChecked } = useApp();
  if (!user && !authChecked) return null; // validating session
  if (!user) return <Navigate to="/" replace />;
  return <Shell>{children}</Shell>;
}

function RootRoute() {
  const { user } = useApp();
  if (user) return <Navigate to="/messages" replace />;
  return <LoginPage />;
}

/**
 * Personal-QR connect links: /?connect={email-or-phone}
 * - Logged in  -> shows a CONFIRMATION dialog (who is it) before connecting
 * - Logged out -> remembers the target and asks right after login
 */
function ConnectLinkHandler() {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState(null); // {code, person}
  const [busy, setBusy] = React.useState(false);
  const loadingRef = React.useRef(false);

  // Step 1: resolve WHO the code belongs to and ask for confirmation
  const tryRedeem = React.useCallback(() => {
    const target = sessionStorage.getItem("pending_connect");
    if (!user || !target || loadingRef.current) return;
    loadingRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/connect/preview", { params: { code: target } });
        setPreview({ code: target, person: data.user });
      } catch (err) {
        sessionStorage.removeItem("pending_connect");
        const detail = err?.response?.data?.detail || "";
        if (detail === "own_code") toast.info(t("own_connect_code"));
        else toast.error(t("invalid_connect_code"));
      } finally {
        loadingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Step 2: user confirmed -> actually connect and open the DM
  const confirmConnect = async () => {
    if (!preview || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/connect/redeem", { code: preview.code });
      sessionStorage.removeItem("pending_connect");
      setPreview(null);
      toast.success(`${t("connected_with")} ${data.user?.name || ""}`);
      navigate(`/chat/${data.conversation.id}`, { replace: true });
    } catch (err) {
      sessionStorage.removeItem("pending_connect");
      setPreview(null);
      const detail = err?.response?.data?.detail || "";
      if (detail === "own_code") toast.info(t("own_connect_code"));
      else if (detail === "invalid_connect_code") toast.error(t("invalid_connect_code"));
      else toast.error(t("google_login_failed"));
    } finally {
      setBusy(false);
    }
  };

  const cancelConnect = () => {
    sessionStorage.removeItem("pending_connect");
    setPreview(null);
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get("connect");
    if (target) {
      sessionStorage.setItem("pending_connect", target);
      window.history.replaceState({}, "", window.location.pathname);
      // Deferred: the <Toaster> mounts after this handler, so an immediate
      // toast fired from this mount effect would be lost.
      if (!user) setTimeout(() => toast.info(t("connect_after_login")), 300);
    }
    // Company join link opened while ALREADY signed in: explain instead of ignoring
    if (user && (params.get("join") || sessionStorage.getItem("pending_join"))) {
      sessionStorage.removeItem("pending_join");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => toast.info(t("join_link_logged_in")), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs after login (user becomes available) AND immediately when a native
  // deep link stores a pending target (see DeepLinkHandler below).
  React.useEffect(() => { tryRedeem(); }, [tryRedeem]);
  React.useEffect(() => {
    const onPending = () => {
      if (!user) toast.info(t("connect_after_login"));
      tryRedeem();
    };
    // Native deep link with ?join= arriving while signed in -> friendly explanation
    const onPendingJoin = () => {
      if (!user) return; // logged out: LoginPage consumes it
      sessionStorage.removeItem("pending_join");
      toast.info(t("join_link_logged_in"));
    };
    window.addEventListener("pending-connect", onPending);
    window.addEventListener("pending-join", onPendingJoin);
    return () => {
      window.removeEventListener("pending-connect", onPending);
      window.removeEventListener("pending-join", onPendingJoin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryRedeem, user]);

  // Confirmation dialog: who you're about to connect with
  return (
    <AlertDialog open={!!preview} onOpenChange={(v) => { if (!v) cancelConnect(); }}>
      <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl" data-testid="connect-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{t("connect_confirm_title")}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              <div className="flex items-center gap-3 rounded-xl border bg-muted/40 px-3 py-2.5 mt-1">
                <div
                  className="h-11 w-11 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
                  style={{ backgroundColor: preview?.person?.avatar_color || "#64748b" }}
                >
                  {(preview?.person?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0 text-left">
                  <div className="text-sm font-semibold text-foreground truncate" data-testid="connect-confirm-name">
                    {preview?.person?.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {[preview?.person?.title, preview?.person?.company_name].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">{t("connect_confirm_hint")}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="connect-confirm-cancel" onClick={cancelConnect}>{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction data-testid="connect-confirm-accept" disabled={busy} onClick={(e) => { e.preventDefault(); confirmConnect(); }}>
            {t("connect")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Native-only (iOS/Android): handles the onsitecomms:// deep links that the
 * backend uses to return OAuth sessions (Google AND Apple) from the SYSTEM browser.
 *   onsitecomms://auth/{provider}#token=...            -> log in and enter the app
 *   onsitecomms://auth/{provider}#{provider}_error=... -> show a friendly error toast
 * It ALSO handles invite links shared via WhatsApp/SMS that open the app:
 *   ...?connect={email-or-phone} -> person-to-person connection (opens the DM)
 *   ...?join={company-code}      -> registration with the company code pre-filled
 */
function DeepLinkHandler() {
  const { login, t } = useApp();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let handle = null;

    // Invite links (?connect= / ?join=) — works for both the custom scheme
    // (onsitecomms://...) and https universal links routed into the app.
    const handleInviteUrl = (url) => {
      try {
        const qIdx = url.indexOf("?");
        if (qIdx === -1) return false;
        const params = new URLSearchParams(url.slice(qIdx + 1).split("#")[0]);
        const connectTarget = params.get("connect");
        const joinCode = params.get("join");
        if (connectTarget) {
          sessionStorage.setItem("pending_connect", connectTarget);
          // Logged in -> ConnectLinkHandler redeems instantly; logged out -> after login
          window.dispatchEvent(new Event("pending-connect"));
          return true;
        }
        if (joinCode) {
          sessionStorage.setItem("pending_join", joinCode);
          window.dispatchEvent(new Event("pending-join"));
          navigate("/", { replace: false });
          return true;
        }
      } catch (e) { /* malformed url — ignore */ }
      return false;
    };

    const handleUrl = async (url) => {
      if (!url) return;
      if (!url.startsWith("onsitecomms://auth/")) {
        handleInviteUrl(url);
        return;
      }
      try {
        const provider = url.includes("/auth/apple") ? "apple" : "google";
        // Close the in-app system browser (best effort)
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch (e) { /* Android Custom Tabs close themselves */ }
        const frag = url.split("#")[1] || "";
        const params = new URLSearchParams(frag);
        const err = params.get(`${provider}_error`);
        if (err) {
          if (err === "account_not_found") toast.error(t("google_account_not_found"));
          else if (err !== "cancelled") toast.error(`${t(`${provider}_login_failed`)} (${err})`);
          return;
        }
        const token = params.get("token");
        if (!token) return;
        setAuthToken(token);
        const { data: user } = await api.get("/auth/me");
        login(token, user);
        navigate("/messages", { replace: true });
      } catch (e) {
        setAuthToken(null);
        toast.error(t("google_login_failed"));
      }
    };

    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        handle = await CapApp.addListener("appUrlOpen", ({ url }) => { handleUrl(url); });
        // Cold start: the launch URL may arrive before the listener registers
        try {
          const launch = await CapApp.getLaunchUrl();
          if (launch?.url && !launch.url.startsWith("onsitecomms://auth/")) handleInviteUrl(launch.url);
        } catch (e) { /* no launch url */ }
      } catch (e) { /* @capacitor/app not available (web) */ }
    })();
    return () => { try { handle && handle.remove(); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function App() {
  React.useEffect(() => { initKeyboardFix(); }, []);
  return (
    <ErrorBoundary>
    <AppProvider>
      <BrowserRouter>
        <ForceUpdateGate />
        <DeepLinkHandler />
        <ConnectLinkHandler />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/auth/apple" element={<AppleCallbackPage />} />
          <Route path="/auth/google" element={<GoogleCallbackPage />} />
          <Route path="/home" element={<Protected><Home /></Protected>} />
          <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
          <Route path="/chat/:convId" element={<Protected><ChatThread /></Protected>} />
          <Route path="/jobs" element={<Protected><JobsPage /></Protected>} />
          <Route path="/jobs/:jobId" element={<Protected><JobHub /></Protected>} />
          <Route path="/issues" element={<Protected><IssuesPage /></Protected>} />
          <Route path="/tasks" element={<Protected><TasksPage /></Protected>} />
          <Route path="/search" element={<Protected><SearchPage /></Protected>} />
          <Route path="/activity" element={<Protected><ActivityPage /></Protected>} />
          <Route path="/summary" element={<Protected><DailySummaryPage /></Protected>} />
          <Route path="/more" element={<Protected><MorePage /></Protected>} />
          <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
        </Routes>
        {/* Toasts sit BELOW the iOS status bar / Dynamic Island (safe-area offset) */}
        <Toaster
          position="top-center"
          richColors
          offset={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
          mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
        />
      </BrowserRouter>
    </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
